import { Component, Input, Output, EventEmitter, Signal, computed, isDevMode } from '@angular/core';
import { BaseComponent } from '../common/base.component';
import { OAuthService } from '../services/o-auth.service';
import { PoeApiService, PoeFilter, FilterUpdateDelayedError } from '../services/poe-api.service';
import { FilterService } from '../services/filter.service';
import { FilterImporterService } from '../services/filter-importer.service';
import { FilterImportResults } from '../models/filter-import.models';
import { LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM, SYNC_WARNING_FOR_NEW_VERSION, DISABLE_SYNC_UNTIL_REALM_DOWN, LOCAL_STORAGE_KEY_FORCE_ALLOW_SYNC, VERSION } from '../const';
import { patchVersion } from '../../version';
import { StoredFilter } from '../models/filter-models';
import { ACCOUNT_FILTER_DEFAULT_REALM, abbrevGameName } from '../../game';
import { logEvent } from '../app.config';
import { TooltipWrapperComponent } from '../common/tooltip-wrapper/tooltip-wrapper.component';
import { FileSaveService } from '../services/file-save.service';
import { DelayedMouseleaveDirective } from '../common/delayed-mouseleave/delayed-mouseleave.directive';

@Component({
  selector: 'app-controls',
  templateUrl: './controls.component.html',
  styleUrl: './controls.component.scss',
  standalone: true,
  imports: [TooltipWrapperComponent, DelayedMouseleaveDirective]
})
export class ControlsComponent extends BaseComponent {
  @Input() showLeft: boolean = true;
  @Input() showCenter: boolean = true;
  @Input() showRight: boolean = true;
  @Input() isBeta: boolean = false;
  @Input() isLegacy: boolean = false;
  @Input() tab: string = '';
  @Input() initialVisit: boolean = false;
  @Input() floating: boolean = false;
  @Output() presetsClick = new EventEmitter<void>();
  @Output() customImport = new EventEmitter<FilterImportResults>();
  @Output('quickImport') quickImportFinished = new EventEmitter<FilterImportResults>();

  storedFilter!: Signal<any>;
  filterResults!: Signal<any>;
  userName: Signal<string | null>;

  saveButtonText: ReturnType<typeof computed<string>>;
  hasLinkedHandle: ReturnType<typeof computed<boolean>>;
  supportsFileSystemAccess: boolean;

  showSaveOptions = false;

  synchronizing = false;
  syncText = `Sync to ${abbrevGameName}`;
  copyText = 'Copy to Clipboard';

  syncTimestamps: number[] = [];
  syncDisabled = false;
  syncWarningUntilRealmDown = SYNC_WARNING_FOR_NEW_VERSION;
  disableSyncUntilRealmDown = DISABLE_SYNC_UNTIL_REALM_DOWN && !localStorage.getItem(LOCAL_STORAGE_KEY_FORCE_ALLOW_SYNC);
  syncCountdown = '';
  syncWarning = '';

  showImportOptions = false;
  isDevMode = isDevMode();

  constructor(
    private loginService: OAuthService,
    private poeApiService: PoeApiService,
    private filterService: FilterService,
    private filterImporterService: FilterImporterService,
    private fileSaveService: FileSaveService
  ) {
    super();
    this.userName = this.loginService.userName;
    this.storedFilter = this.filterService.activeFilter;
    this.filterResults = this.filterService.filterResultData;

    this.supportsFileSystemAccess = this.fileSaveService.supportsFileSystemAccess;

    this.saveButtonText = computed(() => {
      if (!this.fileSaveService.supportsFileSystemAccess) return 'Download';
      const name = this.storedFilter().name;
      return this.fileSaveService.knownHandleNames().has(name) ? 'Save to file' : 'Save to new file';
    });

    this.hasLinkedHandle = computed(() =>
      this.fileSaveService.supportsFileSystemAccess &&
      this.fileSaveService.knownHandleNames().has(this.storedFilter().name)
    );
  }

  syncOrLogin() {
    if (this.userName()) {
      this.synchronizing = true;
      const realm = localStorage.getItem(LOCAL_STORAGE_KEY_FILTER_DEFAULT_REALM) || ACCOUNT_FILTER_DEFAULT_REALM;
      const filterType = this.storedFilter().filter.ruthless ? 'Ruthless' : undefined;
      if (!this.storedFilter().id) {
        logEvent('Filter', 'Sync', 'Creating on ' + realm);
        this.syncText = 'Creating...';
        this.poeApiService.createFilter(this.storedFilter().name, this.filterResults().filterTextFull, realm, filterType)
          .then((poeFilter: PoeFilter) => {
            this.storedFilter().id = poeFilter.id;
            this.filterService.updateFilter();
            this.handleSyncSuccess();
          })
          .catch(error => this.handleSyncError(error))
          .finally(() => this.synchronizing = false);
      } else {
        logEvent('Filter', 'Sync', 'Updating');
        this.syncText = 'Synchronizing...';
        this.poeApiService.updateFilter(this.storedFilter().id!, this.storedFilter().name, this.filterResults().filterTextFull, realm, filterType)
          .then(() => this.handleSyncSuccess())
          .catch(error => this.handleSyncError(error))
          .finally(() => this.synchronizing = false);
      }

      const now = Date.now();
      this.syncTimestamps.push(now);
      this.syncTimestamps = this.syncTimestamps.filter(timestamp => now - timestamp < 600000); // Keep only timestamps within the last 10 minutes

      if (this.syncTimestamps.length >= 2) {
        const waitTime = 600000 - (now - this.syncTimestamps[0]);
        this.syncDisabled = true;
        this.startCountdown(waitTime);
        return;
      }
    } else {
      this.loginService.login();
    }
  }

  private handleSyncSuccess() {
    this.syncWarning = '';
    this.syncText = 'Synchronized!';
    setTimeout(() => this.syncText = `Sync to ${abbrevGameName}`, 1000);
  }

  private handleSyncError(error: any) {
    if (error instanceof FilterUpdateDelayedError) {
      this.syncText = 'Sync Delayed';
      setTimeout(() => this.syncText = `Sync to ${abbrevGameName}`, 1000);
      this.syncWarning = error.message;
    } else {
      const errorMessage = ('Error: ' + (error?.toString() || 'Unspecified')).substring(0, 100);
      console.error(errorMessage);
      logEvent('Filter', 'Failed to sync', errorMessage);
      this.syncText = 'Failed to Sync';
      this.syncWarning = errorMessage;
    }
  }

  private startCountdown(waitTime: number) {
    const interval = setInterval(() => {
      const minutes = Math.floor(waitTime / 60000);
      if (minutes > 0) {
        this.syncCountdown = `Please wait ${minutes + 1} min`;
      } else {
        this.syncCountdown = `Please wait ${Math.floor(waitTime / 1000)} sec`;
      }

      if (waitTime <= 0) {
        clearInterval(interval);
        this.syncDisabled = false;
        this.syncCountdown = '';
      }

      waitTime -= 1000;
    }, 1000);
  }

  save() {
    const extension = this.storedFilter().filter.ruthless ? '.ruthlessfilter' : '.filter';
    const filename = this.storedFilter().name + extension;
    const text = this.filterResults().filterTextFull;

    if (this.fileSaveService.supportsFileSystemAccess) {
      logEvent('Filter', 'Save to file');
      this.fileSaveService.save(text, this.storedFilter().name, filename);
    } else {
      logEvent('Filter', 'Download');
      this.downloadTextAsFile(text, filename);
    }
  }

  saveToNewFile() {
    const extension = this.storedFilter().filter.ruthless ? '.ruthlessfilter' : '.filter';
    const filename = this.storedFilter().name + extension;
    logEvent('Filter', 'Save to new file');
    this.fileSaveService.saveToNewFile(this.filterResults().filterTextFull, this.storedFilter().name, filename);
  }

  private downloadTextAsFile(text: string, filename: string) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);
    element.classList.add('matomo_ignore');

    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  copyToClipboard() {
    logEvent('Filter', 'Copy');
    navigator.clipboard.writeText(this.filterResults().filterTextFull);
    this.copyText = 'Copied!';
    setTimeout(() => this.copyText = 'Copy to Clipboard', 1000);
  }

  clear() {
    if (confirm("Are you sure you want to reset your filter to the defaults? This cannot be undone.")) {
      this.filterService.resetFilter();
    }
  }

  import(callback: Function) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.filter,.ruthlessfilter';
    input.style.display = 'none'; // Hide the input element
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          let result = new FilterImportResults();
          try {
            const content = e.target!.result as string;
            result = this.filterImporterService.importFilter(content);
          } catch (error: any) {
            result.importFilterError = error;
          } finally {
            document.body.removeChild(input); // Remove the input element
            callback(result);
          }
        };
        reader.readAsText(file);
      } else {
        document.body.removeChild(input); // Remove the input element if no file is selected
      }
    };
    document.body.appendChild(input); // Append the input element to the body
    input.click();
  }

  quickImport() {
    logEvent('Import', 'Quick Import');
    this.import((importResult: FilterImportResults) => {
      if (importResult.importFilterSuccess) {
        this.filterService.replaceFilter(importResult.filter!);
      }

      this.quickImportFinished.emit(importResult);
    });
  }

  importWithOptions() {
    logEvent('Import', 'Custom Import');
    this.import((importResult: FilterImportResults) => {
      // Always emit for parent to handle dialog and errors
      this.customImport.emit(importResult);
    });
  }

  importFromClipboard() {
    navigator.clipboard.readText()
      .then(content => {
        let importResult = new FilterImportResults();
        try {
          importResult = this.filterImporterService.importFilterJson(content);
        } catch (error: any) {
          importResult.importFilterError = error?.toString() || 'Unknown error';
        }

        if (!importResult.importFilterSuccess || !importResult.filter) {
          alert(importResult.importFilterError || 'Failed to import filter from clipboard');
          return;
        }

        const baseName = importResult.importedName || this.storedFilter().name;
        const name = this.getUniqueFilterName(baseName);
        const storedFilter = {
          name,
          filter: importResult.filter,
          version: VERSION,
          build: patchVersion,
          lastUpdated: new Date().toISOString(),
        } as StoredFilter;

        this.filterService.addFilter(storedFilter);
        this.filterService.setActiveFilter(name, 'import');
        this.showImportOptions = false;
      })
      .catch(error => {
        alert('Failed to read clipboard: ' + (error?.toString() || 'Unknown error'));
      });
  }

  private getUniqueFilterName(baseName: string): string {
    if (!this.filterService.storedFilters.some(f => f.name === baseName)) {
      return baseName;
    }

    const dateSuffix = this.formatDateSuffix(new Date());
    const suffixedName = `${baseName} ${dateSuffix}`;
    if (!this.filterService.storedFilters.some(f => f.name === suffixedName)) {
      return suffixedName;
    }

    let counter = 2;
    while (this.filterService.storedFilters.some(f => f.name === `${suffixedName} ${counter}`)) {
      counter++;
    }
    return `${suffixedName} ${counter}`;
  }

  private formatDateSuffix(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  onPresetsClick() {
    this.presetsClick.emit();
  }
}

